<script setup lang="ts">
import {
    isNaturalComplianceProfileLocked,
    usePersonalAccountDataState,
} from '../../../../utilities/account/useAccountDataPersonal'
import { CheckCircleIcon, CheckIcon, ExclamationCircleIcon, PencilIcon } from '@heroicons/vue/solid'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import { isNaturalComplianceProfileCategoryComplete } from '../../../wizard/ESAC/form/forms'
import { useVModel } from '@vueuse/core'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import { esacPersonalComplianceProfileConfig } from './esacComplianceProfile'
import { ecspPersonalComplianceProfileConfig } from './ecspComplianceProfile'
import BaseButtonSecondary from '../../../base/BaseButtonSecondary.vue'
import { usePersonalAccountFormState } from '../../../../utilities/account/useAccountFormPersonal'
import { useOpenCloseForm, useSaveForm } from '../../../../utilities/helpers'
import { ref } from 'vue'
import { i18n } from '../../../../localization'

const { naturalProfile: account } = usePersonalAccountDataState()
const accountDataStore = usePersonalAccountDataState()
const accountFormStore = usePersonalAccountFormState()

const props = defineProps<{
    form: any
    pageKey: string
    quickEdit?: boolean
    currentForm?: string
}>()

const currentForm = useVModel(props, 'currentForm')

const emit = defineEmits<{
    (e: 'navigateToForm', form: string): void
}>()

const isValid = ref(false)
setIsValid()

async function setIsValid() {
    if (usePersonalAccountDataState().loading) {
        return null
    }
    isValid.value = await isNaturalComplianceProfileCategoryComplete(props.form.validationKey, props.form.denyKey)
}

const buttonTitle = !props.form.buttonTitle ? i18n.global.t('account_settings.edit') : props.form.buttonTitle

const complianceProfile =
    getPlatformSettings().compliance_profile === 'esac'
        ? esacPersonalComplianceProfileConfig
        : ecspPersonalComplianceProfileConfig

const model =
    getPlatformSettings().compliance_profile === 'esac'
        ? account.compliance_profiles.esac
        : account.compliance_profiles.ecsp

const { closeForm, openForm } = useOpenCloseForm()
const { saveForm } = useSaveForm()

const handleCloseForm = async () => {
    await closeForm(model, currentForm, complianceProfile, accountFormStore)
}
const handleOpenForm = async () => {
    await openForm(model, currentForm, complianceProfile, accountFormStore, emit, props.quickEdit, props.pageKey)
}
const handleSaveForm = async () => {
    await saveForm(currentForm, complianceProfile, accountFormStore, props.quickEdit, setIsValid)
}
</script>

<template>
    <li class="mb-0 @container">
        <div>
            <div class="flex items-center p-2 sm:p-4">
                <div
                    class="flex min-w-0 flex-1 flex-col items-center justify-between gap-y-3 supports-cq:@sm:flex-row supports-not-cq:sm:flex-row">
                    <div class="flex w-full items-center md:w-auto">
                        <div class="flex-shrink-0">
                            <div class="relative flex items-center justify-center rounded-full bg-white p-1">
                                <div class="relative">
                                    <Component :is="form.icon" class="h-12 w-12 text-gray-300" />
                                    <CheckCircleIcon
                                        v-if="!form.hideStatus && isValid === true"
                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-green-500" />
                                    <ExclamationCircleIcon
                                        v-else-if="!form.hideStatus && isValid === false"
                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-red-400" />
                                </div>
                            </div>
                        </div>
                        <div class="min-w-0 flex-1 px-4">
                            <div>
                                <p class="truncate text-sm font-semibold text-gray-900">{{ form.title }}</p>
                                <p v-if="!form.hideStatus" class="mt-1 text-sm text-gray-500">
                                    <i18n-t keypath="compliance_profile.status" tag="p">
                                        <template #status>
                                            <span>{{
                                                isValid
                                                    ? $t('compliance_profile.complete')
                                                    : $t('compliance_profile.incomplete')
                                            }}</span>
                                        </template>
                                    </i18n-t>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="flex w-full justify-end space-x-2">
                        <template v-if="quickEdit && currentForm === pageKey">
                            <BaseButtonMuted class="w-full md:w-auto" @click="handleCloseForm">
                                {{ $t('dialog.button.cancel') }}
                            </BaseButtonMuted>
                            <BaseButtonSecondary
                                class="w-full md:w-auto"
                                :icon="CheckIcon"
                                :loading="accountDataStore.saving"
                                :disabled="accountDataStore.saving || accountFormStore.hasValidationErrors"
                                @click="handleSaveForm">
                                {{ $t('account.save') }}
                            </BaseButtonSecondary>
                        </template>
                        <BaseButtonMuted
                            v-else
                            class="w-full md:w-auto"
                            :disabled="isNaturalComplianceProfileLocked()"
                            :icon="PencilIcon"
                            @click="handleOpenForm">
                            {{ buttonTitle }}
                        </BaseButtonMuted>
                    </div>
                </div>
            </div>
            <div v-if="currentForm === pageKey" class="border-t px-4 pt-3 sm:px-6">
                <component
                    :is="complianceProfile[currentForm].component"
                    v-model="model[complianceProfile[currentForm].validationKey]" />
                <div v-if="quickEdit" class="visible mb-2 mt-6 flex justify-center space-x-2 lg:hidden">
                    <BaseButtonMuted :sticky="true" @click="handleCloseForm">
                        {{ $t('dialog.button.cancel') }}
                    </BaseButtonMuted>
                    <BaseButtonSecondary
                        :icon="CheckIcon"
                        :loading="accountDataStore.saving"
                        :disabled="accountDataStore.saving || accountFormStore.hasValidationErrors"
                        :sticky="true"
                        @click="handleSaveForm">
                        {{ $t('account.save') }}
                    </BaseButtonSecondary>
                </div>
            </div>
        </div>
    </li>
</template>
